import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { webLog } from '../api/grpc/webLogger';
import {
  getBoltProfileMappingDefinitions,
  getBoltProfileObjectTypeName,
  isBoltProfileAttributeName,
} from '../lib/boltProfileMapping';
import { getStorage } from '../lib/utils/safeStorage';
import { sanitizeMappingConfiguration } from '../lib/utils/mappingConfigurationSanitizer';
import { mergeMappingConfiguration } from './configurationPersistence';
import { normalizeRevitParameterName } from '../lib/revitParameterNames';
import { clearAppliedPcfCatalogSections } from './pcfProfileCatalogState';
import { buildDefaultConnectorBindingConfiguration } from './connectorBindingDefaults';
import {
  ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES,
  DEFAULT_BOLT_PROJECT_PARAMETER_CATEGORIES,
} from '../types';
import type {
  AttributeType,
  MappingAttribute,
  ObjectTypeMapping,
  FormField,
  MenuConfiguration,
  ConnectorBindingConfiguration,
  ConnectorParameterTarget,
  ConnectorBindingFormFieldKey,
  ProcessPropertiesConfiguration,
  BoltConfiguration,
  BoltConfigurationFormFieldKey,
  BoltProjectParameterCategory,
  AdditionalCategoriesConfiguration,
  AdditionalCategoriesFormFieldKey,
  AdditionalCategoriesProjectParameterCategory,
  MappingTableConfiguration,
  MappingConfiguration,
  StatusBuilderConfiguration,
  StatusCategory,
  StatusAttributeRow,
} from '../types';

// Re-export types for backward compatibility
export type {
  AttributeType,
  MappingAttribute,
  ObjectTypeMapping,
  FormField,
  MenuConfiguration,
  ConnectorBindingConfiguration,
  ProcessPropertiesConfiguration,
  BoltConfiguration,
  AdditionalCategoriesConfiguration,
  MappingTableConfiguration,
  MappingConfiguration,
  StatusBuilderConfiguration,
  StatusCategory,
  StatusAttributeRow,
};
const EMPTY_MAPPING_ATTRIBUTES: MappingAttribute[] = [];
type ConfigurationDebugWindow = Window & {
  __PIPEEX_MAPPING_TRACE__?: boolean;
  __PIPEEX_WEB_LOG_VERBOSE?: boolean;
};

const isEnabledFlag = (value: string | null | undefined): boolean =>
  value === '1' || value === 'true';

const isConfigurationStoreDebugEnabled = (): boolean => {
  if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
    return false;
  }

  const debugWindow = window as ConfigurationDebugWindow;
  if (debugWindow.__PIPEEX_MAPPING_TRACE__ === true || debugWindow.__PIPEEX_WEB_LOG_VERBOSE === true) {
    return true;
  }

  try {
    return (
      isEnabledFlag(debugWindow.localStorage?.getItem('pipeex:mappingTrace')) ||
      isEnabledFlag(debugWindow.localStorage?.getItem('pipeex:webLogVerbose'))
    );
  } catch {
    return false;
  }
};

const stringifyConfigurationDebugPayload = (payload: unknown): string => {
  try {
    return JSON.stringify(payload ?? {});
  } catch {
    return '"[Unserializable configuration store payload]"';
  }
};

const debugConfigurationStore = (message: string, payload?: unknown): void => {
  if (!isConfigurationStoreDebugEnabled()) {
    return;
  }

  const formattedPayload = stringifyConfigurationDebugPayload(payload);
  webLog.info('configurationStore', `${message} ${formattedPayload}`);
  console.info(`[ConfigurationStore] ${message} ${formattedPayload}`, payload ?? {});
};

// ============================================================================
// State Interface
// ============================================================================

interface ConfigurationState {
  // Menu configuration
  menuConfiguration: MenuConfiguration;

  // Connector binding configuration
  connectorBindingConfiguration: ConnectorBindingConfiguration;

  // Process properties configuration
  processPropertiesConfiguration: ProcessPropertiesConfiguration;

  // Bolt configuration
  boltConfiguration: BoltConfiguration;

  // Additional categories configuration
  additionalCategoriesConfiguration: AdditionalCategoriesConfiguration;

  // Mapping configuration
  mappingConfiguration: MappingConfiguration;

  // Status builder configuration
  statusBuilderConfiguration: StatusBuilderConfiguration;

  // Currently selected object type for mapping
  selectedObjectType: string;

  // Dirty state tracking
  isDirty: boolean;

  // Tracks if configuration has been saved at least once
  // Used to keep completion affordances available after the first successful apply
  hasSavedOnce: boolean;

  // Tracks whether the workflow has been applied and exited the guided mode
  isWorkflowApplied: boolean;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface ConfigurationActions {
  // Menu configuration actions
  updateMenuConfiguration: (config: Partial<MenuConfiguration>) => void;

  // Connector binding actions
  updateConnectorBindingField: (field: ConnectorBindingFormFieldKey, value: string) => void;
  setConnectorBindingParameterTarget: (target: ConnectorParameterTarget) => void;
  setConnectorBindingError: (field: ConnectorBindingFormFieldKey, error: string) => void;
  clearConnectorBindingError: (field: ConnectorBindingFormFieldKey) => void;

  // Process properties actions
  updateProcessPropertiesField: (field: keyof ProcessPropertiesConfiguration, value: string | boolean) => void;
  setProcessPropertiesError: (field: keyof ProcessPropertiesConfiguration, error: string) => void;
  setIncludeDefaultPipeexParameters: (include: boolean | null, options?: { markDirty?: boolean }) => void;

  // Bolt configuration actions
  updateBoltField: (field: BoltConfigurationFormFieldKey, value: string) => void;
  setBoltParameterTarget: (target: ConnectorParameterTarget) => void;
  setBoltProjectParameterCategories: (categories: string[]) => void;
  setBoltError: (field: BoltConfigurationFormFieldKey, error: string) => void;

  // Additional categories actions
  updateAdditionalCategoriesField: (field: AdditionalCategoriesFormFieldKey, value: string) => void;
  setAdditionalCategoriesParameterTarget: (target: ConnectorParameterTarget) => void;
  setAdditionalCategoriesProjectParameterCategories: (categories: string[]) => void;
  setAdditionalCategoriesError: (field: AdditionalCategoriesFormFieldKey, error: string) => void;

  // Mapping configuration actions
  setSelectedObjectType: (objectType: string) => void;
  updateMappingAttribute: (objectTypeName: string, attributeIndex: number, attribute: Partial<MappingAttribute>) => void;
  addMappingAttribute: (objectTypeName: string, attribute: MappingAttribute) => void;
  removeMappingAttribute: (objectTypeName: string, attributeIndex: number) => void;
  reorderMappingAttributes: (objectTypeName: string, fromIndex: number, toIndex: number) => void;
  addObjectType: (objectType: ObjectTypeMapping) => void;
  removeObjectType: (objectTypeName: string) => void;
  updateAdvancedSettings: (config: Partial<MappingConfiguration['advanced']>) => void;

  // Status builder actions
  addStatusCategory: (category: StatusCategory) => void;
  updateStatusCategory: (id: string, updates: Partial<StatusCategory>) => void;
  removeStatusCategory: (id: string) => void;
  addStatusAttribute: (categoryId: string, row: StatusAttributeRow) => void;
  updateStatusAttribute: (categoryId: string, rowId: string, updates: Partial<StatusAttributeRow>) => void;
  removeStatusAttribute: (categoryId: string, rowId: string) => void;
  setStatusBuilderConfiguration: (config: StatusBuilderConfiguration) => void;

  // Bolt configuration sync action
  syncBoltAttributesToMapping: (options?: { markDirty?: boolean }) => void;

  // Additional categories sync action
  syncAdditionalCategoriesToMapping: (options?: { markDirty?: boolean }) => void;

  // State management
  markClean: () => void;
  saveConfiguration: () => void;
  resetConfiguration: () => void;
  resetWizardSelectionsToDefaults: () => void;
  loadConfiguration: (config: Partial<ConfigurationState>) => void;

  // Workflow reinitiation (keeps data but allows re-editing from menu)
  reinitiateWorkflow: () => void;
  resetProjectScopedConfigurationState: () => void;
  restoreWizardStepDefaults: (
    step: 'boltConnection' | 'additionalCategories' | 'processProperties'
  ) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const createDefaultFormField = (value: string): FormField => ({
  value,
  error: undefined,
});

const isFormFieldLike = (value: unknown): value is FormField =>
  typeof value === 'object' && value !== null && 'value' in value;

const backfillMissingFormFieldValues = <T extends object>(current: T, defaults: T): T => {
  let changed = false;
  const next = { ...current } as T;

  (Object.keys(defaults) as Array<keyof T>).forEach((key) => {
    const currentValue = current[key];
    const defaultValue = defaults[key];

    if (!isFormFieldLike(currentValue) || !isFormFieldLike(defaultValue)) {
      return;
    }

    if (typeof currentValue.value !== 'string' || typeof defaultValue.value !== 'string') {
      return;
    }

    if (currentValue.value.trim() !== '' || defaultValue.value.trim() === '') {
      return;
    }

    next[key] = {
      ...currentValue,
      value: defaultValue.value,
    } as T[keyof T];
    changed = true;
  });

  return changed ? next : current;
};

const normalizePcfAttributeKey = (value: string): string =>
  value.trim().toUpperCase().replace(/[\s_-]/g, '');

const isBoltMappingAttribute = (attribute: MappingAttribute): boolean =>
  isBoltProfileAttributeName(attribute.pcfAttributeName);

const summarizeBoltConfigurationForDebug = (boltConfig: BoltConfiguration): Record<string, string> => ({
  boltModelingChoice: boltConfig.boltModelingChoice.value,
  boltBaseFamilyValue: boltConfig.boltBaseFamilyValue.value,
  boltSetActiveValue: boltConfig.boltSetActiveValue.value,
  boltDiaValue: boltConfig.boltDiaValue.value,
  boltQuantityValue: boltConfig.boltQuantityValue.value,
  boltLengthValue: boltConfig.boltLengthValue.value,
  boltItemDescriptionValue: boltConfig.boltItemDescriptionValue.value,
  boltMaterialValue: boltConfig.boltMaterialValue.value,
  boltStandardValue: boltConfig.boltStandardValue.value,
});

const summarizeBoltMappingRowsForDebug = (
  objectTypes: ObjectTypeMapping[]
): Array<{
  objectType: string;
  totalAttributes: number;
  boltAttributes: Array<{
    order: number;
    pcfAttributeName: string;
    revitParam: string;
    attributeType: string;
  }>;
}> =>
  ['BOLT', 'GASKET'].map((objectTypeName) => {
    const objectType = objectTypes.find(
      (candidate) => normalizePcfAttributeKey(candidate.name) === normalizePcfAttributeKey(objectTypeName)
    );
    return {
      objectType: objectTypeName,
      totalAttributes: objectType?.attributes.length ?? 0,
      boltAttributes: (objectType?.attributes ?? []).filter(isBoltMappingAttribute).map((attribute) => ({
        order: attribute.order,
        pcfAttributeName: attribute.pcfAttributeName,
        revitParam: attribute.revitParam,
        attributeType: attribute.attributeType,
      })),
    };
  });

const getObjectTypeAttributes = (
  mappingConfiguration: MappingConfiguration,
  objectTypeName: string
): MappingAttribute[] => {
  const objectTypes = mappingConfiguration.general.mappingTable.objectType;
  const normalizedObjectTypeName = normalizePcfAttributeKey(objectTypeName);

  return (
    objectTypes.find(
      (objectType) => normalizePcfAttributeKey(objectType.name) === normalizedObjectTypeName
    )?.attributes ?? []
  );
};

const getMappingAttributeValue = (
  mappingConfiguration: MappingConfiguration,
  objectTypeName: string,
  ...pcfAttributeNames: string[]
): string | undefined => {
  const normalizedAttributeNames = new Set(
    pcfAttributeNames.map((pcfAttributeName) => normalizePcfAttributeKey(pcfAttributeName))
  );
  const attribute = getObjectTypeAttributes(mappingConfiguration, objectTypeName).find((candidate) =>
    normalizedAttributeNames.has(normalizePcfAttributeKey(candidate.pcfAttributeName ?? ''))
  );

  return typeof attribute?.revitParam === 'string' ? attribute.revitParam : undefined;
};

const CONNECTOR_MAPPING_TARGETS: Partial<
  Record<
    ConnectorBindingFormFieldKey,
    {
      objectTypeName: string;
      pcfAttributeNames: readonly string[];
    }
  >
> = {
  endpoint1Value: { objectTypeName: 'ALL', pcfAttributeNames: ['END-POINT_1'] },
  endpoint2Value: { objectTypeName: 'ALL', pcfAttributeNames: ['END-POINT_2'] },
  endpoint3Value: { objectTypeName: 'ALL', pcfAttributeNames: ['END-POINT_3'] },
  endpoint4Value: { objectTypeName: 'ALL', pcfAttributeNames: ['END-POINT_4'] },
  pressureClassValue: { objectTypeName: 'ALL', pcfAttributeNames: ['PressureClass'] },
  materialValue: { objectTypeName: 'ALL', pcfAttributeNames: ['Material'] },
  designStdValue: { objectTypeName: 'ALL', pcfAttributeNames: ['DesignStd'] },
  itemDescriptionValue: { objectTypeName: 'ALL', pcfAttributeNames: ['ITEM-DESCRIPTION'] },
};

const syncMappingAttributeFromSourceChange = (
  mappingConfiguration: MappingConfiguration,
  objectTypeName: string,
  pcfAttributeNames: readonly string[],
  previousValue: string,
  nextValue: string
): MappingConfiguration => {
  if (previousValue === nextValue) {
    return mappingConfiguration;
  }

  const normalizedObjectTypeName = normalizePcfAttributeKey(objectTypeName);
  const normalizedAttributeNames = new Set(
    pcfAttributeNames.map((pcfAttributeName) => normalizePcfAttributeKey(pcfAttributeName))
  );
  const objectTypes = mappingConfiguration.general.mappingTable.objectType;
  const objectTypeIndex = objectTypes.findIndex(
    (objectType) => normalizePcfAttributeKey(objectType.name) === normalizedObjectTypeName
  );

  if (objectTypeIndex < 0) {
    return mappingConfiguration;
  }

  const attributes = objectTypes[objectTypeIndex].attributes;
  const attributeIndex = attributes.findIndex((attribute) =>
    normalizedAttributeNames.has(normalizePcfAttributeKey(attribute.pcfAttributeName ?? ''))
  );

  if (attributeIndex < 0) {
    return mappingConfiguration;
  }

  const currentAttribute = attributes[attributeIndex];
  if ((currentAttribute.revitParam ?? '') !== previousValue) {
    return mappingConfiguration;
  }

  const nextAttributes = [...attributes];
  nextAttributes[attributeIndex] = {
    ...currentAttribute,
    revitParam: nextValue,
  };

  const nextObjectTypes = [...objectTypes];
  nextObjectTypes[objectTypeIndex] = {
    ...nextObjectTypes[objectTypeIndex],
    attributes: nextAttributes,
  };

  return {
    ...mappingConfiguration,
    general: {
      ...mappingConfiguration.general,
      mappingTable: {
        ...mappingConfiguration.general.mappingTable,
        objectType: nextObjectTypes,
      },
    },
  };
};

const getFirstMatchingMappingAttributeValue = (
  mappingConfiguration: MappingConfiguration,
  objectTypeNames: readonly string[],
  ...pcfAttributeNames: string[]
): string | undefined => {
  for (const objectTypeName of objectTypeNames) {
    const value = getMappingAttributeValue(
      mappingConfiguration,
      objectTypeName,
      ...pcfAttributeNames
    );
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
};

const applyMappedFormFieldValue = <T, K extends keyof T>(
  configuration: T,
  field: K,
  nextValue: string | undefined
): T => {
  // Blank mapping rows must not clear wizard fields: a row can be empty
  // because it was never configured, not because the user cleared the field.
  if (nextValue === undefined || nextValue.trim() === '') {
    return configuration;
  }

  const currentValue = configuration[field];
  if (!currentValue || typeof currentValue !== 'object' || !('value' in currentValue)) {
    return configuration;
  }

  return {
    ...(configuration as object),
    [field]: createDefaultFormField(nextValue),
  } as T;
};

const buildReinitiatedWorkflowState = (
  state: ConfigurationState
): Pick<
  ConfigurationState,
  | 'menuConfiguration'
  | 'connectorBindingConfiguration'
  | 'processPropertiesConfiguration'
  | 'boltConfiguration'
  | 'additionalCategoriesConfiguration'
> => {
  const mappingConfiguration = state.mappingConfiguration;
  const menuConfiguration = {
    ...state.menuConfiguration,
  };

  let connectorBindingConfiguration = state.connectorBindingConfiguration;
  const connectorMappings: Array<{
    field: ConnectorBindingFormFieldKey;
    objectType: string;
    pcfAttributeNames: readonly string[];
  }> = [
    { field: 'endpoint1Value', objectType: 'ALL', pcfAttributeNames: ['END-POINT_1'] },
    { field: 'endpoint2Value', objectType: 'ALL', pcfAttributeNames: ['END-POINT_2'] },
    { field: 'endpoint3Value', objectType: 'ALL', pcfAttributeNames: ['END-POINT_3'] },
    { field: 'endpoint4Value', objectType: 'ALL', pcfAttributeNames: ['END-POINT_4'] },
    { field: 'pressureClassValue', objectType: 'ALL', pcfAttributeNames: ['PressureClass'] },
    { field: 'materialValue', objectType: 'ALL', pcfAttributeNames: ['Material'] },
    { field: 'designStdValue', objectType: 'ALL', pcfAttributeNames: ['DesignStd'] },
    {
      field: 'itemDescriptionValue',
      objectType: 'ALL',
      pcfAttributeNames: ['ITEM-DESCRIPTION'],
    },
  ];

  connectorMappings.forEach(({ field, objectType, pcfAttributeNames }) => {
    connectorBindingConfiguration = applyMappedFormFieldValue(
      connectorBindingConfiguration,
      field,
      getMappingAttributeValue(mappingConfiguration, objectType, ...pcfAttributeNames)
    );
  });

  let boltConfiguration = state.boltConfiguration;
  const boltBasedValue = getMappingAttributeValue(mappingConfiguration, 'BOLT', 'BOLT-MODELING');
  const gasketBasedValue = getMappingAttributeValue(mappingConfiguration, 'GASKET', 'BOLT-MODELING');
  const normalizedBoltModelingValue = normalizePcfAttributeKey(
    boltBasedValue ?? gasketBasedValue ?? ''
  );
  const preferredBoltCategory =
    normalizedBoltModelingValue === 'BOLTBASED'
      ? 'BOLT'
      : normalizedBoltModelingValue === 'GASKETBASED'
        ? 'GASKET'
        : undefined;
  const fallbackBoltCategory = preferredBoltCategory === 'BOLT' ? 'GASKET' : 'BOLT';
  const boltSearchOrder = preferredBoltCategory
    ? [preferredBoltCategory, fallbackBoltCategory]
    : ['BOLT', 'GASKET'];

  if (normalizedBoltModelingValue === 'BOLTBASED') {
    boltConfiguration = {
      ...boltConfiguration,
      boltModelingChoice: createDefaultFormField('yes'),
      boltBaseFamilyValue: createDefaultFormField('BoltBased'),
    };
  } else if (normalizedBoltModelingValue === 'GASKETBASED') {
    boltConfiguration = {
      ...boltConfiguration,
      boltModelingChoice: createDefaultFormField('no'),
      boltBaseFamilyValue: createDefaultFormField('GasketBased'),
    };
  }

  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltDiaValue',
    getFirstMatchingMappingAttributeValue(mappingConfiguration, boltSearchOrder, 'BOLT-DIA')
  );
  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltQuantityValue',
    getFirstMatchingMappingAttributeValue(mappingConfiguration, boltSearchOrder, 'BOLT-QUANTITY')
  );
  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltLengthValue',
    getFirstMatchingMappingAttributeValue(mappingConfiguration, boltSearchOrder, 'BOLT-LENGTH')
  );
  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltSetActiveValue',
    getFirstMatchingMappingAttributeValue(mappingConfiguration, boltSearchOrder, 'BOLT-SET-ACTIVE')
  );
  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltItemDescriptionValue',
    getFirstMatchingMappingAttributeValue(
      mappingConfiguration,
      boltSearchOrder,
      'BOLT-ITEM-DESCRIPTION',
      'ITEM-DESCRIPTION'
    )
  );
  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltMaterialValue',
    getFirstMatchingMappingAttributeValue(
      mappingConfiguration,
      boltSearchOrder,
      'BOLT-MATERIAL',
      'MATERIAL'
    )
  );
  boltConfiguration = applyMappedFormFieldValue(
    boltConfiguration,
    'boltStandardValue',
    getFirstMatchingMappingAttributeValue(
      mappingConfiguration,
      boltSearchOrder,
      'BOLT-STANDARD',
      'STANDARD'
    )
  );

  let additionalCategoriesConfiguration = state.additionalCategoriesConfiguration;
  additionalCategoriesConfiguration = applyMappedFormFieldValue(
    additionalCategoriesConfiguration,
    'valveTagValue',
    getMappingAttributeValue(mappingConfiguration, 'VALVE', 'TAG')
  );
  additionalCategoriesConfiguration = applyMappedFormFieldValue(
    additionalCategoriesConfiguration,
    'supportsTagValue',
    getMappingAttributeValue(mappingConfiguration, 'SUPPORT', 'TAG')
  );
  additionalCategoriesConfiguration = applyMappedFormFieldValue(
    additionalCategoriesConfiguration,
    'supportsSsrefValue',
    getMappingAttributeValue(mappingConfiguration, 'SUPPORT', 'SSREF')
  );
  additionalCategoriesConfiguration = applyMappedFormFieldValue(
    additionalCategoriesConfiguration,
    'instrumentTagValue',
    getMappingAttributeValue(mappingConfiguration, 'INSTRUMENT', 'TAG')
  );

  const processPropertiesConfiguration = {
    ...state.processPropertiesConfiguration,
  };

  return {
    menuConfiguration,
    connectorBindingConfiguration,
    processPropertiesConfiguration,
    boltConfiguration,
    additionalCategoriesConfiguration,
  };
};

const initialConnectorBindingConfiguration =
  buildDefaultConnectorBindingConfiguration(createDefaultFormField);

const initialProcessPropertiesConfiguration: ProcessPropertiesConfiguration = {
  processConditionsChecked: false,
  projectPropertiesChecked: false,
  includeDefaultPipeexParameters: null,
  processConditionsValue: createDefaultFormField('PiX_GN_2_OperationTemperature'),
  projectPropertiesValue: createDefaultFormField('PiX_GN_2_InsulationSpec'),
  pipingSpecValuePC: createDefaultFormField('PiP_PPG_SPEC'),
  tracingSpecValuePC: createDefaultFormField('PiX_GN_2_HeatTracingSpec'),
  attribute12Value: createDefaultFormField('PiX_GN_2_DesignPressure'),
  attribute13Value: createDefaultFormField('PiX_GN_2_DesignTemperature'),
  attribute14Value: createDefaultFormField('PiX_GN_2_OperationPressure'),
  attribute15Value: createDefaultFormField('PiX_GN_2_OperationTemperature'),
  attribute16Value: createDefaultFormField('PiX_GN_1_ServiceDescription'),
  attribute17Value: createDefaultFormField('PiX_GN_1_PED_Group'),
  attribute18Value: createDefaultFormField('PiX_GN_1_PED_Category'),
  attribute19Value: createDefaultFormField('PiX_GN_3_TestPressure'),
  attribute20Value: createDefaultFormField('PiX_GN_3_TestType'),
  attribute21Value: createDefaultFormField('PiX_GN_2_HeatTracingSpec'),
  attribute22Value: createDefaultFormField('PiX_GN_2_InsulationThickness'),
  attribute23Value: createDefaultFormField('PiX_GN_2_InsutlationType'),
  projectIdentifierValue: createDefaultFormField('PiX_TB_1_TITLE_1'),
  projectNameValue: createDefaultFormField('PiX_TB_2_TITLE_2'),
  areaValue: createDefaultFormField('PiX_TB_3_ProjectLocation'),
  attribute1ClientName: createDefaultFormField('PiX_TB_4_ClientName'),
  attribute2ProjectAddress: createDefaultFormField('PiX_TB_5_ClientAddress'),
  attribute3ClientDepartment: createDefaultFormField('PiX_TB_6_ClientDepartment'),
  attribute4ClientProjectNumber: createDefaultFormField('PiX_TB_7_ClientProjectNumber'),
  attribute5ClientDocumentNumber: createDefaultFormField('PiX_TB_8_ClientDocumentNumber'),
  attribute6ClientCode: createDefaultFormField('PiX_TB_9_ClientCode'),
  attribute7EngPartyOffice: createDefaultFormField('PiX_TB_10_EngPartyOffice'),
  attribute8EngPartyOfficeLocation: createDefaultFormField('PiX_TB_11_EngPartyOfficeLocation'),
  attribute9EngPartyProjectNumber: createDefaultFormField('PiX_TB_12_EngPartyProjectNumber'),
  attribute10EngPartyAreaCode: createDefaultFormField('PiX_TB_13_EngPartyAreaCode'),
};

const normalizeBoltProjectParameterCategories = (
  categories: unknown
): BoltProjectParameterCategory[] => {
  if (!Array.isArray(categories)) {
    return [...DEFAULT_BOLT_PROJECT_PARAMETER_CATEGORIES];
  }

  const allowedCategories = new Set<string>(BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS);
  const normalizedCategories = Array.from(
    new Set(
      categories.filter(
        (category): category is BoltProjectParameterCategory =>
          typeof category === 'string' && allowedCategories.has(category)
      )
    )
  );

  return normalizedCategories.length > 0
    ? normalizedCategories
    : [...DEFAULT_BOLT_PROJECT_PARAMETER_CATEGORIES];
};

const normalizeAdditionalCategoriesProjectParameterCategories = (
  categories: unknown
): AdditionalCategoriesProjectParameterCategory[] => {
  if (!Array.isArray(categories)) {
    return [...DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES];
  }

  const allowedCategories = new Set<string>(ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS);
  const normalizedCategories = Array.from(
    new Set(
      categories.filter(
        (category): category is AdditionalCategoriesProjectParameterCategory =>
          typeof category === 'string' && allowedCategories.has(category)
      )
    )
  );

  return normalizedCategories.length > 0
    ? normalizedCategories
    : [...DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES];
};

const areStringArraysEqual = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const initialBoltConfiguration: BoltConfiguration = {
  parameterDestination: 'project',
  parameterBindingKind: 'type',
  projectParameterCategories: [...DEFAULT_BOLT_PROJECT_PARAMETER_CATEGORIES],
  boltModelingChoice: createDefaultFormField('no'),
  boltBaseFamilyValue: createDefaultFormField('BoltBased'),
  boltSetActiveValue: createDefaultFormField('PiX_PPG_920_BoltSet_ON'),
  boltItemDescriptionValue: createDefaultFormField('PiX_PPG_914_BoltSet_Description'),
  boltDiaValue: createDefaultFormField('PiX_PPG_912_BoltSet_Diameter'),
  boltQuantityValue: createDefaultFormField('PiX_PPG_910_BoltSet_Quantity'),
  boltLengthValue: createDefaultFormField('PiX_PPG_911_BoltSet_BoltLength'),
  boltMaterialValue: createDefaultFormField('PiX_PPG_916_BoltSet_Material'),
  boltStandardValue: createDefaultFormField('PiX_PPG_915_BoltSet_Standard'),
};

const initialAdditionalCategoriesConfiguration: AdditionalCategoriesConfiguration = {
  parameterDestination: 'project',
  parameterBindingKind: 'instance',
  projectParameterCategories: [...DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES],
  valveTagValue: createDefaultFormField('PiX_GN_4_ItemTag'),
  supportsTagValue: createDefaultFormField('PiX_GN_4_ItemTag'),
  supportsSsrefValue: createDefaultFormField('PiX_PPG_Sup_SSREF'),
  instrumentTagValue: createDefaultFormField('PiX_GN_4_ItemTag'),
};

const normalizeConnectorParameterAliasFormField = (
  field: FormField | undefined,
  fallback: FormField
): FormField => {
  const source =
    field && typeof field === 'object' && 'value' in field
      ? field
      : fallback;
  const normalizedValue = normalizeRevitParameterName(source.value);
  return normalizedValue === source.value
    ? source
    : { ...source, value: normalizedValue };
};

const reseedConnectorBindingParameterAliases = (
  configuration: ConnectorBindingConfiguration
): ConnectorBindingConfiguration => ({
  ...configuration,
  insulationSpecValue: normalizeConnectorParameterAliasFormField(
    configuration.insulationSpecValue,
    initialConnectorBindingConfiguration.insulationSpecValue
  ),
  insulationThicknessValue: normalizeConnectorParameterAliasFormField(
    configuration.insulationThicknessValue,
    initialConnectorBindingConfiguration.insulationThicknessValue
  ),
});

const initialStatusBuilderConfiguration: StatusBuilderConfiguration = {
  categories: [
    {
      id: 'existing',
      name: 'Existing',
      color: 'success',
      isBuiltIn: true,
      description: 'Elements that already exist in the facility',
      phaseKind: 'Created',
      phaseNames: ['Existing'],
      attributes: [],
    },
    {
      id: 'demolition',
      name: 'Demolition',
      color: 'error',
      isBuiltIn: true,
      description: 'Elements marked for removal',
      phaseKind: 'Demolished',
      phaseNames: [],
      attributes: [],
    },
  ],
};

/**
 * Older persisted states predate phaseKind/phaseNames/attributes on categories —
 * normalize every category so components can rely on the fields existing.
 */
const normalizeStatusCategory = (category: Partial<StatusCategory> & { id: string; name: string }): StatusCategory => ({
  color: 'info',
  isBuiltIn: false,
  ...category,
  phaseKind: category.phaseKind === 'Demolished' ? 'Demolished' : 'Created',
  phaseNames: Array.isArray(category.phaseNames) ? category.phaseNames : [],
  attributes: Array.isArray(category.attributes) ? category.attributes : [],
} as StatusCategory);

const initialMappingConfiguration: MappingConfiguration = {
  general: {
    mappingTable: {
      addButton: 'Add',
      objectType: [
        {
          name: 'ALL',
          attributes: [
            // Endpoint attributes (not in General section) - Pull from connector binding defaults
            { order: 1, pcfAttributeName: 'END-POINT_1', revitParam: initialConnectorBindingConfiguration.endpoint1Value.value, attributeType: 'Parameter', fx: '' },
            { order: 2, pcfAttributeName: 'END-POINT_2', revitParam: initialConnectorBindingConfiguration.endpoint2Value.value, attributeType: 'Parameter', fx: '' },
            { order: 3, pcfAttributeName: 'END-POINT_3', revitParam: initialConnectorBindingConfiguration.endpoint3Value.value, attributeType: 'Parameter', fx: '' },
            { order: 4, pcfAttributeName: 'END-POINT_4', revitParam: initialConnectorBindingConfiguration.endpoint4Value.value, attributeType: 'Parameter', fx: '' },
            // BOM columns (not in General section) - Pull from connector binding defaults
            { order: 5, pcfAttributeName: 'PressureClass', revitParam: initialConnectorBindingConfiguration.pressureClassValue.value, attributeType: 'BomColumn', fx: '' },
            { order: 6, pcfAttributeName: 'Material', revitParam: initialConnectorBindingConfiguration.materialValue.value, attributeType: 'BomColumn', fx: '' },
            { order: 7, pcfAttributeName: 'DesignStd', revitParam: initialConnectorBindingConfiguration.designStdValue.value, attributeType: 'BomColumn', fx: '' },
            // Item description (not in General section) - Pull from connector binding defaults
            { order: 8, pcfAttributeName: 'ITEM-DESCRIPTION', revitParam: initialConnectorBindingConfiguration.itemDescriptionValue.value, attributeType: 'Parameter', fx: '' },
            // Note: OBJECT-TYPE, SKEY, PIPING-SPEC, INSULATION-SPEC, TRACING-SPEC, WEIGHT, INSULATIONTHICKNESS
            // are now in the General Attributes section
          ],
        },
        { name: 'BOLT', attributes: [] },
        { name: 'GASKET', attributes: [] },
        { name: 'MESSAGE', attributes: [] },
        { name: 'VALVE', attributes: [] },
        { name: 'SUPPORT', attributes: [] },
        { name: 'INSTRUMENT', attributes: [] },
        { name: 'END-CONNECTION-EQUIPMENT', attributes: [] },
        { name: 'PIPE', attributes: [] },
      ],
    },
    pipingSystemProperties: [
      { id: '1', name: 'System Name', value: '' },
      { id: '2', name: 'System Type', value: '' },
      { id: '3', name: 'Fluid Type', value: '' },
      { id: '4', name: 'Design Pressure', value: '' },
      { id: '5', name: 'Design Temperature', value: '' },
    ],
    projectProperties: [
      { id: '1', name: 'Project Name', value: '' },
      { id: '2', name: 'Project Number', value: '' },
      { id: '3', name: 'Client Name', value: '' },
      { id: '4', name: 'Location', value: '' },
      { id: '5', name: 'Engineer', value: '' },
    ],
  },
  advanced: {
    centerOfCoordinates: 'ProjectBasePoint',
  },
  actions: {
    saveSetting: 'Save setting',
    cancel: 'Cancel',
  },
};

export const DEFAULT_MENU_CONFIGURATION: MenuConfiguration = {
  selectedOption: null,
  objectTypeValue: '#PCF_OBJECT_TYPE',
  sKeyValue: '#PCF_SKEY',
  pipingSpecValue: 'PiP_PPG_SPEC',
  systemGroupingValue: 'BySystemName',
  groupingParameterName: '',
  groupingParameterSource: 'Element',
};

const initialState: ConfigurationState = {
  menuConfiguration: DEFAULT_MENU_CONFIGURATION,
  connectorBindingConfiguration: initialConnectorBindingConfiguration,
  processPropertiesConfiguration: initialProcessPropertiesConfiguration,
  boltConfiguration: initialBoltConfiguration,
  additionalCategoriesConfiguration: initialAdditionalCategoriesConfiguration,
  mappingConfiguration: initialMappingConfiguration,
  statusBuilderConfiguration: initialStatusBuilderConfiguration,
  selectedObjectType: 'ALL',
  isDirty: false,
  hasSavedOnce: false,
  isWorkflowApplied: false,
};

// ============================================================================
// Storage Version for Migrations
// ============================================================================

const STORAGE_VERSION = 4;
const isConnectorParameterDestination = (value: unknown): value is ConnectorParameterTarget['destination'] =>
  value === 'project' || value === 'family';

const isConnectorParameterBindingKind = (value: unknown): value is ConnectorParameterTarget['bindingKind'] =>
  value === 'type' || value === 'instance';

const mergeBoltConfiguration = (
  persistedBoltConfiguration: unknown,
  connectorConfiguration: ConnectorBindingConfiguration
): BoltConfiguration => {
  const persistedBolt =
    persistedBoltConfiguration && typeof persistedBoltConfiguration === 'object'
      ? (persistedBoltConfiguration as Partial<BoltConfiguration>)
      : {};
  const seededDestination = isConnectorParameterDestination(persistedBolt.parameterDestination)
    ? persistedBolt.parameterDestination
    : connectorConfiguration.parameterDestination;
  const seededBindingKind = isConnectorParameterBindingKind(persistedBolt.parameterBindingKind)
    ? persistedBolt.parameterBindingKind
    : connectorConfiguration.parameterBindingKind;

  return {
    ...initialBoltConfiguration,
    ...persistedBolt,
    parameterDestination: seededDestination,
    parameterBindingKind: seededBindingKind,
    projectParameterCategories: normalizeBoltProjectParameterCategories(
      persistedBolt.projectParameterCategories
    ),
  };
};

const mergeAdditionalCategoriesConfiguration = (
  persistedAdditionalCategoriesConfiguration: unknown
): AdditionalCategoriesConfiguration => {
  const persistedAdditionalCategories =
    persistedAdditionalCategoriesConfiguration &&
    typeof persistedAdditionalCategoriesConfiguration === 'object'
      ? (persistedAdditionalCategoriesConfiguration as Partial<AdditionalCategoriesConfiguration>)
      : {};
  const seededDestination = isConnectorParameterDestination(
    persistedAdditionalCategories.parameterDestination
  )
    ? persistedAdditionalCategories.parameterDestination
    : initialAdditionalCategoriesConfiguration.parameterDestination;
  const seededBindingKind = isConnectorParameterBindingKind(
    persistedAdditionalCategories.parameterBindingKind
  )
    ? persistedAdditionalCategories.parameterBindingKind
    : initialAdditionalCategoriesConfiguration.parameterBindingKind;
  return {
    ...initialAdditionalCategoriesConfiguration,
    ...persistedAdditionalCategories,
    parameterDestination: seededDestination,
    parameterBindingKind: seededBindingKind,
    projectParameterCategories: normalizeAdditionalCategoriesProjectParameterCategories(
      persistedAdditionalCategories.projectParameterCategories
    ),
  };
};

// Helper function to validate and merge persisted state
const validateAndMergeState = (
  persistedState: unknown,
  currentState: ConfigurationState & ConfigurationActions
): ConfigurationState & ConfigurationActions => {
  // If persisted state is null/undefined, return current state
  if (!persistedState || typeof persistedState !== 'object') {
    debugConfigurationStore('[PipeEX] No valid persisted state found, using initial state');
    return currentState;
  }

  const persisted = persistedState as Partial<ConfigurationState>;

  try {
    // Validate critical fields exist and have correct structure
    const merged: Partial<ConfigurationState> = {};

    // Merge menuConfiguration
    if (persisted.menuConfiguration && typeof persisted.menuConfiguration === 'object') {
      merged.menuConfiguration = {
        ...DEFAULT_MENU_CONFIGURATION,
        ...persisted.menuConfiguration,
      };
    }

    // Merge connectorBindingConfiguration
    if (persisted.connectorBindingConfiguration && typeof persisted.connectorBindingConfiguration === 'object') {
      merged.connectorBindingConfiguration = reseedConnectorBindingParameterAliases({
        ...initialConnectorBindingConfiguration,
        ...persisted.connectorBindingConfiguration,
      });
    }

    // Merge processPropertiesConfiguration
    if (persisted.processPropertiesConfiguration && typeof persisted.processPropertiesConfiguration === 'object') {
      merged.processPropertiesConfiguration = {
        ...initialProcessPropertiesConfiguration,
        ...persisted.processPropertiesConfiguration,
      };
    }

    // Merge boltConfiguration
    merged.boltConfiguration = mergeBoltConfiguration(
      persisted.boltConfiguration,
      merged.connectorBindingConfiguration ?? initialConnectorBindingConfiguration
    );

    // Merge additionalCategoriesConfiguration
    merged.additionalCategoriesConfiguration = mergeAdditionalCategoriesConfiguration(
      persisted.additionalCategoriesConfiguration
    );

    // Merge statusBuilderConfiguration. Older persisted categories predate
    // phaseKind/phaseNames/attributes — inherit built-in defaults by id, then
    // normalize so every category carries the full new shape.
    if (persisted.statusBuilderConfiguration && typeof persisted.statusBuilderConfiguration === 'object') {
      const persistedCategories = Array.isArray((persisted.statusBuilderConfiguration as StatusBuilderConfiguration).categories)
        ? (persisted.statusBuilderConfiguration as StatusBuilderConfiguration).categories
        : initialStatusBuilderConfiguration.categories;
      merged.statusBuilderConfiguration = {
        categories: persistedCategories.map((cat) => {
          const builtIn = initialStatusBuilderConfiguration.categories.find((b) => b.id === cat?.id);
          return normalizeStatusCategory({ ...(builtIn ?? {}), ...cat });
        }),
      };
    }

    // Merge mapping configuration to preserve user-edited and auto-created tables
    // while still retaining missing defaults from the current schema.
    merged.mappingConfiguration = sanitizeMappingConfiguration(mergeMappingConfiguration(
      persisted.mappingConfiguration,
      initialMappingConfiguration
    ));

    // Merge selectedObjectType
    if (persisted.selectedObjectType && typeof persisted.selectedObjectType === 'string') {
      merged.selectedObjectType = persisted.selectedObjectType;
    }

    if (typeof persisted.hasSavedOnce === 'boolean') {
      merged.hasSavedOnce = persisted.hasSavedOnce;
    }

    if (typeof persisted.isWorkflowApplied === 'boolean') {
      merged.isWorkflowApplied = persisted.isWorkflowApplied;
    }

    return {
      ...currentState,
      ...merged,
    };
  } catch (error) {
    console.error('[PipeEX] Error merging persisted state, using initial state:', error);
    return currentState;
  }
};

// ============================================================================
// Store Creation
// ============================================================================

export const useConfigurationStore = create<ConfigurationState & ConfigurationActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Menu configuration actions
      updateMenuConfiguration: (config) => {
        set((state) => ({
          menuConfiguration: { ...state.menuConfiguration, ...config },
          isDirty: true,
        }));
      },

      // Connector binding actions
      updateConnectorBindingField: (field, value) => {
        set((state) => {
          const mappingTarget = CONNECTOR_MAPPING_TARGETS[field];
          const currentField = state.connectorBindingConfiguration[field];
          const previousValue = isFormFieldLike(currentField) ? currentField.value : '';
          if (previousValue === value && isFormFieldLike(currentField) && currentField.error === undefined) {
            return state;
          }

          const mappingConfiguration = mappingTarget
            ? syncMappingAttributeFromSourceChange(
                state.mappingConfiguration,
                mappingTarget.objectTypeName,
                mappingTarget.pcfAttributeNames,
                previousValue,
                value
              )
            : state.mappingConfiguration;

          return {
            connectorBindingConfiguration: {
              ...state.connectorBindingConfiguration,
              [field]: { value, error: undefined },
            },
            mappingConfiguration,
            isDirty: true,
          };
        });
      },

      setConnectorBindingParameterTarget: (target) => {
        set((state) => ({
          connectorBindingConfiguration: {
            ...state.connectorBindingConfiguration,
            parameterDestination: target.destination,
            parameterBindingKind: target.bindingKind,
          },
          isDirty: true,
        }));
      },

      setConnectorBindingError: (field, error) => {
        set((state) => {
          const currentField = state.connectorBindingConfiguration[field];
          if (isFormFieldLike(currentField) && currentField.error === error) {
            return state;
          }

          return {
            connectorBindingConfiguration: {
              ...state.connectorBindingConfiguration,
              [field]: { ...currentField, error },
            },
          };
        });
      },

      clearConnectorBindingError: (field) => {
        set((state) => {
          const currentField = state.connectorBindingConfiguration[field];
          if (isFormFieldLike(currentField) && currentField.error === undefined) {
            return state;
          }

          return {
            connectorBindingConfiguration: {
              ...state.connectorBindingConfiguration,
              [field]: { ...currentField, error: undefined },
            },
          };
        });
      },

      // Process properties actions
      updateProcessPropertiesField: (field, value) => {
        set((state) => {
          const currentValue = state.processPropertiesConfiguration[field];
          debugConfigurationStore('[ProcessPropertiesStore] updateProcessPropertiesField', {
            field,
            previousValue:
              typeof currentValue === 'boolean'
                ? currentValue
                : currentValue?.value,
            nextValue: value,
          });
          if (typeof currentValue === 'boolean' || currentValue === null) {
            if (currentValue === value) {
              return state;
            }

            return {
              processPropertiesConfiguration: {
                ...state.processPropertiesConfiguration,
                [field]: value as boolean,
              },
              isDirty: true,
            };
          }
          if (isFormFieldLike(currentValue) && currentValue.value === value && currentValue.error === undefined) {
            return state;
          }

          return {
            processPropertiesConfiguration: {
              ...state.processPropertiesConfiguration,
              [field]: { value: value as string, error: undefined },
            },
            isDirty: true,
          };
        });
      },

      setProcessPropertiesError: (field, error) => {
        set((state) => {
          const currentValue = state.processPropertiesConfiguration[field];
          if (isFormFieldLike(currentValue)) {
            if (currentValue.error === error) {
              return state;
            }

            return {
              processPropertiesConfiguration: {
                ...state.processPropertiesConfiguration,
                [field]: { ...currentValue, error },
              },
            };
          }
          return state;
        });
      },

      setIncludeDefaultPipeexParameters: (include, options) => {
        set((state) => {
          if (state.processPropertiesConfiguration.includeDefaultPipeexParameters === include) {
            return state;
          }

          return {
            processPropertiesConfiguration: {
              ...state.processPropertiesConfiguration,
              includeDefaultPipeexParameters: include,
            },
            isDirty: options?.markDirty === false ? state.isDirty : true,
          };
        });
      },

      // Bolt configuration actions
      updateBoltField: (field, value) => {
        set((state) => {
          const currentField = state.boltConfiguration[field];
          if (isFormFieldLike(currentField) && currentField.value === value && currentField.error === undefined) {
            return state;
          }

          return {
            boltConfiguration: {
              ...state.boltConfiguration,
              [field]: { value, error: undefined },
            },
            isDirty: true,
          };
        });
      },

      setBoltParameterTarget: (target) => {
        set((state) => {
          if (
            state.boltConfiguration.parameterDestination === target.destination &&
            state.boltConfiguration.parameterBindingKind === target.bindingKind
          ) {
            return state;
          }

          return {
            boltConfiguration: {
              ...state.boltConfiguration,
              parameterDestination: target.destination,
              parameterBindingKind: target.bindingKind,
            },
            isDirty: true,
          };
        });
      },

      setBoltProjectParameterCategories: (categories) => {
        set((state) => {
          const projectParameterCategories = normalizeBoltProjectParameterCategories(categories);
          if (areStringArraysEqual(state.boltConfiguration.projectParameterCategories, projectParameterCategories)) {
            return state;
          }

          return {
            boltConfiguration: {
              ...state.boltConfiguration,
              projectParameterCategories,
            },
            isDirty: true,
          };
        });
      },

      setBoltError: (field, error) => {
        set((state) => {
          const currentField = state.boltConfiguration[field];
          if (isFormFieldLike(currentField) && currentField.error === error) {
            return state;
          }

          return {
            boltConfiguration: {
              ...state.boltConfiguration,
              [field]: { ...currentField, error },
            },
          };
        });
      },

      // Additional categories actions
      updateAdditionalCategoriesField: (field, value) => {
        set((state) => {
          const currentField = state.additionalCategoriesConfiguration[field];
          if (isFormFieldLike(currentField) && currentField.value === value && currentField.error === undefined) {
            return state;
          }

          return {
            additionalCategoriesConfiguration: {
              ...state.additionalCategoriesConfiguration,
              [field]: { value, error: undefined },
            },
            isDirty: true,
          };
        });
      },

      setAdditionalCategoriesParameterTarget: (target) => {
        set((state) => {
          if (
            state.additionalCategoriesConfiguration.parameterDestination === target.destination &&
            state.additionalCategoriesConfiguration.parameterBindingKind === target.bindingKind
          ) {
            return state;
          }

          return {
            additionalCategoriesConfiguration: {
              ...state.additionalCategoriesConfiguration,
              parameterDestination: target.destination,
              parameterBindingKind: target.bindingKind,
            },
            isDirty: true,
          };
        });
      },

      setAdditionalCategoriesProjectParameterCategories: (categories) => {
        set((state) => {
          const projectParameterCategories =
            normalizeAdditionalCategoriesProjectParameterCategories(categories);
          if (
            areStringArraysEqual(
              state.additionalCategoriesConfiguration.projectParameterCategories,
              projectParameterCategories
            )
          ) {
            return state;
          }

          return {
            additionalCategoriesConfiguration: {
              ...state.additionalCategoriesConfiguration,
              projectParameterCategories,
            },
            isDirty: true,
          };
        });
      },

      setAdditionalCategoriesError: (field, error) => {
        set((state) => {
          const currentField = state.additionalCategoriesConfiguration[field];
          if (isFormFieldLike(currentField) && currentField.error === error) {
            return state;
          }

          return {
            additionalCategoriesConfiguration: {
              ...state.additionalCategoriesConfiguration,
              [field]: { ...currentField, error },
            },
          };
        });
      },

      // Mapping configuration actions
      setSelectedObjectType: (objectType) => {
        set({ selectedObjectType: objectType });
      },

      updateMappingAttribute: (objectTypeName, attributeIndex, attribute) => {
        set((state) => {
          const objectTypes = [...state.mappingConfiguration.general.mappingTable.objectType];
          const objectTypeIndex = objectTypes.findIndex((ot) => ot.name === objectTypeName);

          if (objectTypeIndex === -1) return state;

          const updatedAttributes = [...objectTypes[objectTypeIndex].attributes];
          const currentAttribute = updatedAttributes[attributeIndex];
          if (!currentAttribute) return state;

          const hasChanges = Object.entries(attribute).some(
            ([key, value]) => currentAttribute[key as keyof MappingAttribute] !== value
          );
          if (!hasChanges) return state;

          updatedAttributes[attributeIndex] = {
            ...currentAttribute,
            ...attribute,
          };

          objectTypes[objectTypeIndex] = {
            ...objectTypes[objectTypeIndex],
            attributes: updatedAttributes,
          };

          return {
            mappingConfiguration: {
              ...state.mappingConfiguration,
              general: {
                ...state.mappingConfiguration.general,
                mappingTable: {
                  ...state.mappingConfiguration.general.mappingTable,
                  objectType: objectTypes,
                },
              },
            },
            isDirty: true,
          };
        });
      },

      addMappingAttribute: (objectTypeName, attribute) => {
        set((state) => {
          const objectTypes = [...state.mappingConfiguration.general.mappingTable.objectType];
          const objectTypeIndex = objectTypes.findIndex((ot) => ot.name === objectTypeName);

          if (objectTypeIndex === -1) return state;

          const newAttribute = {
            ...attribute,
            order: objectTypes[objectTypeIndex].attributes.length + 1,
          };

          objectTypes[objectTypeIndex] = {
            ...objectTypes[objectTypeIndex],
            attributes: [...objectTypes[objectTypeIndex].attributes, newAttribute],
          };

          return {
            mappingConfiguration: {
              ...state.mappingConfiguration,
              general: {
                ...state.mappingConfiguration.general,
                mappingTable: {
                  ...state.mappingConfiguration.general.mappingTable,
                  objectType: objectTypes,
                },
              },
            },
            isDirty: true,
          };
        });
      },

      removeMappingAttribute: (objectTypeName, attributeIndex) => {
        set((state) => {
          const objectTypes = [...state.mappingConfiguration.general.mappingTable.objectType];
          const objectTypeIndex = objectTypes.findIndex((ot) => ot.name === objectTypeName);

          if (objectTypeIndex === -1) return state;

          const updatedAttributes = objectTypes[objectTypeIndex].attributes
            .filter((_, index) => index !== attributeIndex)
            .map((attr, index) => ({ ...attr, order: index + 1 }));

          objectTypes[objectTypeIndex] = {
            ...objectTypes[objectTypeIndex],
            attributes: updatedAttributes,
          };

          return {
            mappingConfiguration: {
              ...state.mappingConfiguration,
              general: {
                ...state.mappingConfiguration.general,
                mappingTable: {
                  ...state.mappingConfiguration.general.mappingTable,
                  objectType: objectTypes,
                },
              },
            },
            isDirty: true,
          };
        });
      },

      reorderMappingAttributes: (objectTypeName, fromIndex, toIndex) => {
        set((state) => {
          const objectTypes = [...state.mappingConfiguration.general.mappingTable.objectType];
          const objectTypeIndex = objectTypes.findIndex((ot) => ot.name === objectTypeName);

          if (objectTypeIndex === -1) return state;

          const attributes = [...objectTypes[objectTypeIndex].attributes];
          const [removed] = attributes.splice(fromIndex, 1);
          attributes.splice(toIndex, 0, removed);

          const reorderedAttributes = attributes.map((attr, index) => ({
            ...attr,
            order: index + 1,
          }));

          objectTypes[objectTypeIndex] = {
            ...objectTypes[objectTypeIndex],
            attributes: reorderedAttributes,
          };

          return {
            mappingConfiguration: {
              ...state.mappingConfiguration,
              general: {
                ...state.mappingConfiguration.general,
                mappingTable: {
                  ...state.mappingConfiguration.general.mappingTable,
                  objectType: objectTypes,
                },
              },
            },
            isDirty: true,
          };
        });
      },

      addObjectType: (objectType) => {
        set((state) => ({
          mappingConfiguration: {
            ...state.mappingConfiguration,
            general: {
              ...state.mappingConfiguration.general,
              mappingTable: {
                ...state.mappingConfiguration.general.mappingTable,
                objectType: [
                  ...state.mappingConfiguration.general.mappingTable.objectType,
                  objectType,
                ],
              },
            },
          },
          isDirty: true,
        }));
      },

      removeObjectType: (objectTypeName) => {
        set((state) => ({
          mappingConfiguration: {
            ...state.mappingConfiguration,
            general: {
              ...state.mappingConfiguration.general,
              mappingTable: {
                ...state.mappingConfiguration.general.mappingTable,
                objectType: state.mappingConfiguration.general.mappingTable.objectType.filter(
                  (ot) => ot.name !== objectTypeName
                ),
              },
            },
          },
          selectedObjectType:
            state.selectedObjectType === objectTypeName ? 'ALL' : state.selectedObjectType,
          isDirty: true,
        }));
      },

      updateAdvancedSettings: (config) => {
        set((state) => ({
          mappingConfiguration: {
            ...state.mappingConfiguration,
            advanced: {
              ...state.mappingConfiguration.advanced,
              ...config,
            },
          },
          isDirty: true,
        }));
      },

      // Status builder actions
      addStatusCategory: (category) => {
        set((state) => ({
          statusBuilderConfiguration: {
            ...state.statusBuilderConfiguration,
            categories: [...state.statusBuilderConfiguration.categories, category],
          },
          isDirty: true,
        }));
      },

      updateStatusCategory: (id, updates) => {
        set((state) => ({
          statusBuilderConfiguration: {
            ...state.statusBuilderConfiguration,
            categories: state.statusBuilderConfiguration.categories.map((cat) =>
              cat.id === id ? { ...cat, ...updates } : cat
            ),
          },
          isDirty: true,
        }));
      },

      removeStatusCategory: (id) => {
        set((state) => ({
          statusBuilderConfiguration: {
            ...state.statusBuilderConfiguration,
            categories: state.statusBuilderConfiguration.categories.filter(
              (cat) => cat.id !== id
            ),
          },
          isDirty: true,
        }));
      },

      addStatusAttribute: (categoryId, row) => {
        set((state) => ({
          statusBuilderConfiguration: {
            ...state.statusBuilderConfiguration,
            categories: state.statusBuilderConfiguration.categories.map((cat) =>
              cat.id === categoryId
                ? { ...cat, attributes: [...(cat.attributes ?? []), row] }
                : cat
            ),
          },
          isDirty: true,
        }));
      },

      updateStatusAttribute: (categoryId, rowId, updates) => {
        set((state) => ({
          statusBuilderConfiguration: {
            ...state.statusBuilderConfiguration,
            categories: state.statusBuilderConfiguration.categories.map((cat) =>
              cat.id === categoryId
                ? {
                    ...cat,
                    attributes: (cat.attributes ?? []).map((row) =>
                      row.id === rowId ? { ...row, ...updates } : row
                    ),
                  }
                : cat
            ),
          },
          isDirty: true,
        }));
      },

      removeStatusAttribute: (categoryId, rowId) => {
        set((state) => ({
          statusBuilderConfiguration: {
            ...state.statusBuilderConfiguration,
            categories: state.statusBuilderConfiguration.categories.map((cat) =>
              cat.id === categoryId
                ? { ...cat, attributes: (cat.attributes ?? []).filter((row) => row.id !== rowId) }
                : cat
            ),
          },
          isDirty: true,
        }));
      },

      setStatusBuilderConfiguration: (config) => {
        set(() => ({
          statusBuilderConfiguration: {
            categories: (config?.categories ?? []).map((cat) => normalizeStatusCategory(cat)),
          },
        }));
      },

      // Bolt configuration sync action
      syncBoltAttributesToMapping: (options) => {
        set((state) => {
          const boltConfig = state.boltConfiguration;
          const isBoltBased = boltConfig.boltModelingChoice.value === 'yes';
          const targetCategory = getBoltProfileObjectTypeName(isBoltBased);
          const sourceCategory = getBoltProfileObjectTypeName(!isBoltBased);
          debugConfigurationStore('[ConfigurationStore] syncBoltAttributesToMapping start', {
            isBoltBased,
            targetCategory,
            sourceCategory,
            boltConfig: summarizeBoltConfigurationForDebug(boltConfig),
            beforeRows: summarizeBoltMappingRowsForDebug(
              state.mappingConfiguration.general.mappingTable.objectType
            ),
          });

          const boltAttributes: MappingAttribute[] =
            getBoltProfileMappingDefinitions(isBoltBased).map((definition, index) => ({
              order: index + 1,
              pcfAttributeName: definition.pcfAttributeName,
              revitParam: boltConfig[definition.field].value,
              attributeType: definition.attributeType,
              fx: '',
            }));

          const objectTypes = [...state.mappingConfiguration.general.mappingTable.objectType];

          // Find or create target category
          let targetIndex = objectTypes.findIndex((ot) => ot.name === targetCategory);
          if (targetIndex === -1) {
            objectTypes.push({ name: targetCategory, attributes: [] });
            targetIndex = objectTypes.length - 1;
          }

          // Find source category
          const sourceIndex = objectTypes.findIndex((ot) => ot.name === sourceCategory);

          // Clear bolt attributes from SOURCE category (reversibility)
          if (sourceIndex !== -1) {
            const nonBoltAttributes = objectTypes[sourceIndex].attributes.filter(
              (attr) => !isBoltMappingAttribute(attr)
            );
            objectTypes[sourceIndex] = {
              ...objectTypes[sourceIndex],
              attributes: nonBoltAttributes.map((attr, idx) => ({ ...attr, order: idx + 1 })),
            };
          }

          // Clear existing bolt attributes from TARGET category, then add new ones
          const existingNonBoltAttributes = objectTypes[targetIndex].attributes.filter(
            (attr) => !isBoltMappingAttribute(attr)
          );

          // Merge: existing non-bolt attributes + new bolt attributes
          const mergedAttributes = [...existingNonBoltAttributes, ...boltAttributes].map(
            (attr, idx) => ({ ...attr, order: idx + 1 })
          );

          objectTypes[targetIndex] = {
            ...objectTypes[targetIndex],
            attributes: mergedAttributes,
          };
          debugConfigurationStore('[ConfigurationStore] syncBoltAttributesToMapping complete', {
            isBoltBased,
            targetCategory,
            sourceCategory,
            afterRows: summarizeBoltMappingRowsForDebug(objectTypes),
          });

          return {
            mappingConfiguration: {
              ...state.mappingConfiguration,
              general: {
                ...state.mappingConfiguration.general,
                mappingTable: {
                  ...state.mappingConfiguration.general.mappingTable,
                  objectType: objectTypes,
                },
              },
            },
            isDirty: options?.markDirty !== false || state.isDirty,
          };
        });
      },

      // Additional categories sync action
      syncAdditionalCategoriesToMapping: (options) => {
        set((state) => {
          const additionalConfig = state.additionalCategoriesConfiguration;
          const objectTypes = [...state.mappingConfiguration.general.mappingTable.objectType];

          // Helper to find or create category
          const findOrCreateCategory = (categoryName: string): number => {
            let index = objectTypes.findIndex((ot) => ot.name === categoryName);
            if (index === -1) {
              objectTypes.push({ name: categoryName, attributes: [] });
              index = objectTypes.length - 1;
            }
            return index;
          };

          // Helper to update category attributes
          const updateCategoryAttributes = (
            categoryIndex: number,
            newAttributes: MappingAttribute[]
          ) => {
            const pcfNames = newAttributes.map((a) => a.pcfAttributeName);
            // Keep existing attributes that aren't being updated
            const existingOther = objectTypes[categoryIndex].attributes.filter(
              (attr) => !pcfNames.includes(attr.pcfAttributeName)
            );
            // Merge and reorder
            const merged = [...existingOther, ...newAttributes].map((attr, idx) => ({
              ...attr,
              order: idx + 1,
            }));
            objectTypes[categoryIndex] = {
              ...objectTypes[categoryIndex],
              attributes: merged,
            };
          };

          // VALVE category - TAG attribute
          const valveIndex = findOrCreateCategory('VALVE');
          updateCategoryAttributes(valveIndex, [
            { order: 1, pcfAttributeName: 'TAG', revitParam: additionalConfig.valveTagValue.value, attributeType: 'Parameter', fx: '' },
          ]);

          // SUPPORT category - TAG and SSREF attributes
          const supportsIndex = findOrCreateCategory('SUPPORT');
          const supportsAttrs: MappingAttribute[] = [
            { order: 1, pcfAttributeName: 'TAG', revitParam: additionalConfig.supportsTagValue.value, attributeType: 'Parameter', fx: '' },
          ];
          if (additionalConfig.supportsSsrefValue.value) {
            supportsAttrs.push(
              { order: 2, pcfAttributeName: 'SSREF', revitParam: additionalConfig.supportsSsrefValue.value, attributeType: 'Parameter', fx: '' }
            );
          }
          updateCategoryAttributes(supportsIndex, supportsAttrs);

          // INSTRUMENT category - TAG attribute
          const instrumentIndex = findOrCreateCategory('INSTRUMENT');
          updateCategoryAttributes(instrumentIndex, [
            { order: 1, pcfAttributeName: 'TAG', revitParam: additionalConfig.instrumentTagValue.value, attributeType: 'Parameter', fx: '' },
          ]);

          return {
            mappingConfiguration: {
              ...state.mappingConfiguration,
              general: {
                ...state.mappingConfiguration.general,
                mappingTable: {
                  ...state.mappingConfiguration.general.mappingTable,
                  objectType: objectTypes,
                },
              },
            },
            isDirty: options?.markDirty !== false || state.isDirty,
          };
        });
      },

      // State management
      markClean: () => {
        set({ isDirty: false });
      },

      saveConfiguration: () => {
        set({ isDirty: false, hasSavedOnce: true, isWorkflowApplied: true });
      },

      resetConfiguration: () => {
        clearAppliedPcfCatalogSections();
        set(initialState);
      },

      resetWizardSelectionsToDefaults: () => {
        clearAppliedPcfCatalogSections();
        // Selecting the default shared-parameters file invalidates every
        // custom-file-driven choice made across the wizard pages, so all
        // page-scoped selections return to the shipped PipeEX defaults.
        // The chosen workflow and applied/saved flags are not selections and
        // stay untouched.
        set((state) => ({
          menuConfiguration: {
            ...DEFAULT_MENU_CONFIGURATION,
            selectedOption: state.menuConfiguration.selectedOption,
          },
          connectorBindingConfiguration: initialConnectorBindingConfiguration,
          processPropertiesConfiguration: initialProcessPropertiesConfiguration,
          boltConfiguration: initialBoltConfiguration,
          additionalCategoriesConfiguration: initialAdditionalCategoriesConfiguration,
          mappingConfiguration: initialMappingConfiguration,
          statusBuilderConfiguration: initialStatusBuilderConfiguration,
          selectedObjectType: initialState.selectedObjectType,
          isDirty: true,
        }));
      },

      loadConfiguration: (config) => {
        set((state) => ({
          ...state,
          ...config,
          mappingConfiguration: config.mappingConfiguration
            ? sanitizeMappingConfiguration(config.mappingConfiguration)
            : state.mappingConfiguration,
          isDirty: false,
        }));
      },

      reinitiateWorkflow: () => {
        set((state) => ({
          ...buildReinitiatedWorkflowState(state),
          isWorkflowApplied: false,
        }));
      },

      resetProjectScopedConfigurationState: () => {
        // Flow enablement is profile-owned; project/document refresh must not clear it.
      },

      restoreWizardStepDefaults: (step) => {
        set((state) => {
          if (step === 'boltConnection') {
            const nextBoltConfiguration = backfillMissingFormFieldValues(
              state.boltConfiguration,
              initialBoltConfiguration
            );

            if (nextBoltConfiguration === state.boltConfiguration) {
              return state;
            }

            return {
              boltConfiguration: nextBoltConfiguration,
            };
          }

          if (step === 'additionalCategories') {
            const nextAdditionalCategoriesConfiguration = backfillMissingFormFieldValues(
              state.additionalCategoriesConfiguration,
              initialAdditionalCategoriesConfiguration
            );

            if (nextAdditionalCategoriesConfiguration === state.additionalCategoriesConfiguration) {
              return state;
            }

            return {
              additionalCategoriesConfiguration: nextAdditionalCategoriesConfiguration,
            };
          }

          const nextProcessPropertiesConfiguration = backfillMissingFormFieldValues(
            state.processPropertiesConfiguration,
            initialProcessPropertiesConfiguration
          );

          if (nextProcessPropertiesConfiguration === state.processPropertiesConfiguration) {
            return state;
          }

          return {
            processPropertiesConfiguration: nextProcessPropertiesConfiguration,
          };
        });
      },
    }),
    {
      name: 'pipeex-configuration-store',
      version: STORAGE_VERSION,
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // Persist all configuration
        menuConfiguration: state.menuConfiguration,
        connectorBindingConfiguration: state.connectorBindingConfiguration,
        processPropertiesConfiguration: state.processPropertiesConfiguration,
        boltConfiguration: state.boltConfiguration,
        additionalCategoriesConfiguration: state.additionalCategoriesConfiguration,
        mappingConfiguration: state.mappingConfiguration,
        statusBuilderConfiguration: state.statusBuilderConfiguration,
        selectedObjectType: state.selectedObjectType,
        hasSavedOnce: state.hasSavedOnce,
        isWorkflowApplied: state.isWorkflowApplied,
      }),
      // Merge persisted state with initial state to handle schema changes
      merge: (persistedState, currentState) => {
        return validateAndMergeState(persistedState, currentState);
      },
      // Handle rehydration errors
      onRehydrateStorage: () => {
        debugConfigurationStore('[PipeEX] Starting state rehydration from localStorage');
        return (state, error) => {
          if (error) {
            console.error('[PipeEX] Rehydration error, clearing corrupted state:', error);
            // Clear corrupted localStorage on error
            try {
              localStorage.removeItem('pipeex-configuration-store');
            } catch {
              // Ignore removal errors
            }
          } else {
            debugConfigurationStore('[PipeEX] State rehydration completed successfully');
          }
        };
      },
      // Migrate state between versions
      migrate: (persistedState, version) => {
        debugConfigurationStore(`[PipeEX] Migrating state from version ${version} to ${STORAGE_VERSION}`);

        // If version is undefined or 0, it's legacy state - reset to initial
        if (version === undefined || version === 0) {
          debugConfigurationStore('[PipeEX] Legacy state detected, resetting to initial state');
          return initialState;
        }

        // Migration from v1 to v2: Remove general attributes from ALL object type
        // (OBJECT-TYPE, SKEY, PIPING-SPEC, INSULATION-SPEC, TRACING-SPEC, WEIGHT, INSULATIONTHICKNESS)
        // as they are now in the separate General Attributes section
        if (version === 1) {
          debugConfigurationStore('[PipeEX] Migrating from v1 to v2: Updating ALL object type attributes');
          const state = persistedState as ConfigurationState;

          // Reset mapping configuration to use new ALL attributes structure
          return {
            ...state,
            mappingConfiguration: initialMappingConfiguration,
          };
        }

        if (version === 2) {
          const state = persistedState as ConfigurationState;
          return {
            ...state,
            mappingConfiguration: sanitizeMappingConfiguration(state.mappingConfiguration),
          };
        }

        if (version === 3) {
          return persistedState as ConfigurationState;
        }

        return persistedState as ConfigurationState;
      },
    }
  )
);

// ============================================================================
// Selector Hooks (for optimized re-renders)
// ============================================================================

export const useMenuConfiguration = () =>
  useConfigurationStore((state) => state.menuConfiguration);

export const useConnectorBindingConfiguration = () =>
  useConfigurationStore((state) => state.connectorBindingConfiguration);

export const useProcessPropertiesConfiguration = () =>
  useConfigurationStore((state) => state.processPropertiesConfiguration);

export const useBoltConfiguration = () =>
  useConfigurationStore((state) => state.boltConfiguration);

export const useAdditionalCategoriesConfiguration = () =>
  useConfigurationStore((state) => state.additionalCategoriesConfiguration);

export const useStatusBuilderConfiguration = () =>
  useConfigurationStore((state) => state.statusBuilderConfiguration);

// Individual action selectors to avoid creating new objects on each render
export const useAddStatusCategory = () =>
  useConfigurationStore((state) => state.addStatusCategory);

export const useUpdateStatusCategory = () =>
  useConfigurationStore((state) => state.updateStatusCategory);

export const useRemoveStatusCategory = () =>
  useConfigurationStore((state) => state.removeStatusCategory);

export const useAddStatusAttribute = () =>
  useConfigurationStore((state) => state.addStatusAttribute);

export const useUpdateStatusAttribute = () =>
  useConfigurationStore((state) => state.updateStatusAttribute);

export const useRemoveStatusAttribute = () =>
  useConfigurationStore((state) => state.removeStatusAttribute);

export const useMappingConfiguration = () =>
  useConfigurationStore((state) => state.mappingConfiguration);

export const useSelectedObjectType = () =>
  useConfigurationStore((state) => state.selectedObjectType);

export const useConfigurationIsDirty = () =>
  useConfigurationStore((state) => state.isDirty);

export const useCurrentObjectTypeAttributes = () =>
  useConfigurationStore((state) => {
    const selectedType = state.selectedObjectType;
    const objectTypes = state.mappingConfiguration?.general?.mappingTable?.objectType;
    const objectType = Array.isArray(objectTypes)
      ? objectTypes.find((ot) => ot && ot.name === selectedType)
      : undefined;
    return objectType?.attributes ?? EMPTY_MAPPING_ATTRIBUTES;
  });

// Hook for workflow state management (for MappingConfigurationPage)
export const useWorkflowState = () => {
  const hasSavedOnce = useConfigurationStore((state) => state.hasSavedOnce);
  const isWorkflowApplied = useConfigurationStore((state) => state.isWorkflowApplied);
  const reinitiateWorkflow = useConfigurationStore((state) => state.reinitiateWorkflow);

  return {
    hasSavedOnce,
    isWorkflowApplied,
    reinitiateWorkflow,
  };
};
